import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Mail, Pause, Play, Send, Clock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, EmptyState, Skeleton } from '@/components/ui/card';
import { PageHeader } from '@/components/ui/page-header';
import { StatusBadge } from '@/components/ui/status-badge';
import { api } from '@/lib/utils';
import { BRAND } from '@maintenance-tracker/shared';

interface CampaignStep {
  id: string;
  stepOrder: number;
  offsetDays: number;
  channel: string;
  templateKey: string;
  recipient: string;
  enabled: boolean;
  offerType?: string;
  offerValue?: number | null;
  offerCode?: string | null;
}

interface Campaign {
  id: string;
  name: string;
  type: string;
  status: string;
  isSystem: boolean;
  stepCount: number;
  enrolledCount: number;
  steps: CampaignStep[];
}

function formatOffset(days: number, campaignType?: string): string {
  if (campaignType === 'winback') {
    if (days === 0) return 'On lapse day';
    return `${days} days after lapse`;
  }
  if (campaignType === 'membership' || campaignType === 'upgrade') {
    if (days === 0) return 'Immediately';
    return `${days} days after start`;
  }
  if (days === 0) return 'On renewal day';
  if (days < 0) return `${Math.abs(days)} days before renewal`;
  return `${days} days after renewal`;
}

function formatOffer(step: CampaignStep): string | null {
  if (!step.offerType || step.offerType === 'none' || step.offerValue == null) return null;
  if (step.offerType === 'percent') return `${step.offerValue}% off`;
  if (step.offerType === 'fixed') return `$${step.offerValue} off`;
  return null;
}

function templateLabel(key: string): string {
  return key.replace(/_/g, ' ').replace(/^renewal /, '');
}

export function CampaignsPage() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ['campaigns'],
    queryFn: () => api<{ campaigns: Campaign[] }>('/api/v1/campaigns'),
  });

  const toggle = useMutation({
    mutationFn: ({ id, status }: { id: string; status: 'active' | 'paused' }) =>
      api(`/api/v1/campaigns/${id}`, { method: 'PATCH', body: JSON.stringify({ status }) }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['campaigns'] }),
  });

  const campaigns = data?.campaigns ?? [];

  return (
    <div className="space-y-6">
      <PageHeader
        title="Campaigns"
        description="Automated email sequences that remind customers to renew — so you don't have to call everyone by hand."
      />

      {isLoading ? (
        <div className="space-y-4">
          {[1, 2].map((i) => (
            <Skeleton key={i} className="h-48 w-full rounded-2xl" />
          ))}
        </div>
      ) : campaigns.length ? (
        campaigns.map((campaign) => (
          <Card
            key={campaign.id}
            title={campaign.name}
            description={
              campaign.isSystem
                ? 'Built-in sequence — runs automatically for every active agreement.'
                : 'Custom renewal sequence.'
            }
            action={
              <div className="flex items-center gap-2">
                <StatusBadge status={campaign.status === 'active' ? 'active' : 'paused'} />
                <Button
                  variant="secondary"
                  size="sm"
                  loading={toggle.isPending}
                  onClick={() =>
                    toggle.mutate({
                      id: campaign.id,
                      status: campaign.status === 'active' ? 'paused' : 'active',
                    })
                  }
                >
                  {campaign.status === 'active' ? (
                    <>
                      <Pause className="h-3.5 w-3.5" /> Pause
                    </>
                  ) : (
                    <>
                      <Play className="h-3.5 w-3.5" /> Resume
                    </>
                  )}
                </Button>
              </div>
            }
          >
            <div className="mb-4 flex flex-wrap gap-4 text-[13px] text-text-secondary">
              <span className="inline-flex items-center gap-1.5">
                <Send className="h-3.5 w-3.5" /> {campaign.enrolledCount} agreements enrolled
              </span>
              <span className="inline-flex items-center gap-1.5">
                <Mail className="h-3.5 w-3.5" /> {campaign.stepCount} email steps
              </span>
            </div>

            <div className="relative space-y-0">
              {campaign.steps.map((step, idx) => (
                <div key={step.id} className="relative flex gap-4 pb-5 last:pb-0">
                  {idx < campaign.steps.length - 1 && (
                    <span className="absolute left-[15px] top-8 h-[calc(100%-8px)] w-px bg-border" />
                  )}
                  <div className="relative z-10 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-brand-subtle text-brand">
                    <Clock className="h-3.5 w-3.5" />
                  </div>
                  <div className="min-w-0 flex-1 pt-0.5">
                    <p className="text-sm font-medium">{formatOffset(step.offsetDays, campaign.type)}</p>
                    <p className="text-[13px] text-text-secondary">
                      Email to {step.recipient === 'owner' ? 'you (owner alert)' : 'customer'} ·{' '}
                      {templateLabel(step.templateKey)}
                      {formatOffer(step) ? ` · Offer: ${formatOffer(step)}` : ''}
                    </p>
                  </div>
                </div>
              ))}
            </div>

            <p className="mt-4 rounded-lg bg-bg-subtle px-3 py-2 text-[12px] text-text-muted">
              {BRAND.name} stops this sequence automatically when a customer renews or you pause the agreement.
            </p>
          </Card>
        ))
      ) : (
        <EmptyState
          icon={Mail}
          title="No campaigns yet"
          description="Your default renewal sequence will appear here once you add your first agreement."
        />
      )}
    </div>
  );
}
